import type { AuthMeResponse, CredentialInfo, MaintenanceInfo, RunnerState } from '@omni/shared';
import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useMe } from '../api/hooks';
import { useStreamState } from '../stream/StreamProvider';

/**
 * Session facts every screen needs, resolved once.
 *
 * Credential / maintenance / runner state are **stream-first**: `hello` and
 * `credential.changed` (CONTRACTS §5) are the live truth, and `GET /api/auth/me`
 * is only the value we started from. That ordering is what lets a screen react
 * to an admin pasting an API key without a reload — and it is exactly what the
 * `budgetsEnforced` contract requires, since flipping the credential kind must
 * re-enable the budget fields the moment the event lands (CONTRACTS §4.1).
 *
 * `SessionFromParts` exists so the `me` half can be supplied directly: it is how
 * a test drives the stream through the real reducer while keeping the REST half
 * fixed, without either side being mocked into agreement.
 */

export interface SessionValue {
  readonly me: AuthMeResponse | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly credential: CredentialInfo | null;
  /** CONTRACTS §4.1 — the single flag every budget surface must consult. */
  readonly budgetsEnforced: boolean;
  readonly maintenance: MaintenanceInfo;
  readonly runnerState: RunnerState | null;
  readonly pendingRecreate: boolean;
  readonly isAdmin: boolean;
}

const SessionContext = createContext<SessionValue>({
  me: null,
  isLoading: true,
  error: null,
  credential: null,
  budgetsEnforced: false,
  maintenance: null,
  runnerState: null,
  pendingRecreate: false,
  isAdmin: false,
});

export function SessionFromParts({
  me,
  isLoading = false,
  error = null,
  children,
}: {
  me: AuthMeResponse | null;
  isLoading?: boolean;
  error?: Error | null;
  children: ReactNode;
}): JSX.Element {
  const stream = useStreamState();

  const value = useMemo<SessionValue>(() => {
    const credential = stream.credential ?? me?.credential ?? null;
    return {
      me,
      isLoading,
      error,
      credential,
      budgetsEnforced: credential?.budgetsEnforced ?? false,
      maintenance: stream.helloAt !== null ? stream.maintenance : (me?.maintenance ?? null),
      runnerState: stream.employee?.state ?? me?.employee.runnerState ?? null,
      pendingRecreate: stream.employee?.pendingRecreate ?? me?.employee.pendingRecreate ?? false,
      isAdmin: me?.user.role === 'admin',
    };
  }, [me, isLoading, error, stream]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const meQuery = useMe();
  return (
    <SessionFromParts
      me={meQuery.data ?? null}
      isLoading={meQuery.isLoading}
      error={meQuery.error ?? null}
    >
      {children}
    </SessionFromParts>
  );
}

export function SessionValueProvider({
  value,
  children,
}: {
  value: SessionValue;
  children: ReactNode;
}): JSX.Element {
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  return useContext(SessionContext);
}
